/**
 * ContactEditPage
 *
 * お問い合わせ (/contact/edit)
 * Allows clinic users to send a support request to the support team.
 * UI follows the DesignTemplate pattern: AppCard (isHeaderColor) + label + AppComponent.
 */

import React from 'react';
import { Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useAuth } from '@/shared/providers/AuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useSendSupportRequest } from '@/hooks/main/useSendSupportRequest';
import useAppToast from '@/hooks/useAppToast';
import { supportRequestSchema, type SupportRequestFormData } from '@/schemas/supportRequestSchema';
import {
  AppButton,
  AppCard,
  AppSelect,
  AppTextArea,
  AppTextField,
  AppRow,
  AppCol,
  AppFormLabel,
  AppForm,
} from '@/components/atoms';
import BottomBar from '@/components/organisms/Footer';
import { mbConvertKana, normalizeEmailInput } from '@/utils/japaneseText';
import { SUPPORT_REQUEST_TITLE_OPTIONS } from '@/constants/supportRequest';

const ContactEditPage: React.FC = () => {
  const { clinic } = useAuth();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const sendMutation = useSendSupportRequest();
  const toast = useAppToast();

  const defaultEmail = clinic?.clinic_information?.email ?? '';
  const clinicDisplayName = clinic?.clinic_information?.name ?? '';

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useTrimmedForm<SupportRequestFormData>({
    resolver: zodResolver(supportRequestSchema),
    defaultValues: {
      title: '',
      body: '',
      name: '',
      re_email: defaultEmail,
    },
  });

  const onSubmit = async (data: SupportRequestFormData) => {
    await sendMutation.mutateAsync(data);
    navigate(`/${tenant}/contact/complete`);
  };

  const onInvalid = () => {
    toast.error('送信ができませんでした。');
  };

  const isPending = isSubmitting || sendMutation.isPending;

  return (
    <div className="space-y-4">
      <AppCard title="お問い合わせ" isHeaderColor>
        <form
          id="contact-edit-form"
          onSubmit={handleSubmit(onSubmit, onInvalid)}
          noValidate
          className="mt-2"
        >
          <AppRow gutter={[0, 16]}>
            {/* 件名 */}
            <AppCol span={24}>
              <AppRow gutter={16} align="middle">
                <AppCol span={6}>
                  <AppFormLabel label="タイトル" required htmlFor="contact-title" />
                </AppCol>
                <AppCol span={18}>
                  <AppForm.Item
                    validateStatus={errors.title ? 'error' : ''}
                    help={errors.title?.message}
                  >
                    <Controller
                      name="title"
                      control={control}
                      render={({ field }) => (
                        <AppSelect
                          {...field}
                          id="contact-title"
                          options={SUPPORT_REQUEST_TITLE_OPTIONS}
                          allowClear={false}
                          className="w-full"
                        />
                      )}
                    />
                  </AppForm.Item>
                </AppCol>
              </AppRow>
            </AppCol>

            {/* 内容 */}
            <AppCol span={24}>
              <AppRow gutter={16} align="top">
                <AppCol span={6}>
                  <div className="pt-2">
                    <AppFormLabel label="お問い合わせ内容" required htmlFor="contact-body" />
                  </div>
                </AppCol>
                <AppCol span={18}>
                  <AppForm.Item
                    validateStatus={errors.body ? 'error' : ''}
                    help={errors.body?.message}
                  >
                    <Controller
                      name="body"
                      control={control}
                      render={({ field }) => (
                        <AppTextArea {...field} id="contact-body" rows={6} className="w-full" />
                      )}
                    />
                  </AppForm.Item>
                </AppCol>
              </AppRow>
            </AppCol>

            {/* 治療院ID (read-only) */}
            <AppCol span={24}>
              <AppRow gutter={16} align="middle">
                <AppCol span={6}>
                  <AppFormLabel label="治療院ＩＤ" htmlFor="contact-clinic-number" />
                </AppCol>
                <AppCol span={18}>
                  <AppTextField
                    id="contact-clinic-number"
                    value={clinic?.clinic_number ?? ''}
                    disabled
                    className="w-full"
                  />
                </AppCol>
              </AppRow>
            </AppCol>

            {/* 治療院名 (read-only) */}
            <AppCol span={24}>
              <AppRow gutter={16} align="middle">
                <AppCol span={6}>
                  <AppFormLabel label="治療院名" htmlFor="contact-clinic-name" />
                </AppCol>
                <AppCol span={18}>
                  <AppTextField
                    id="contact-clinic-name"
                    value={clinicDisplayName}
                    disabled
                    className="w-full"
                  />
                </AppCol>
              </AppRow>
            </AppCol>

            {/* お名前 (optional) */}
            <AppCol span={24}>
              <AppRow gutter={16} align="middle">
                <AppCol span={6}>
                  <AppFormLabel label="お名前" htmlFor="contact-sender-name" />
                </AppCol>
                <AppCol span={18}>
                  <AppForm.Item
                    validateStatus={errors.name ? 'error' : ''}
                    help={errors.name?.message}
                  >
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <AppTextField
                          {...field}
                          id="contact-sender-name"
                          className="w-full"
                          onBlur={e => {
                            field.onChange(mbConvertKana(e.target.value, 'ank'));
                            field.onBlur();
                          }}
                        />
                      )}
                    />
                  </AppForm.Item>
                </AppCol>
              </AppRow>
            </AppCol>

            {/* 返信アドレス */}
            <AppCol span={24}>
              <AppRow gutter={16} align="middle">
                <AppCol span={6}>
                  <AppFormLabel label="返信メールアドレス" required htmlFor="contact-re-email" />
                </AppCol>
                <AppCol span={18}>
                  <AppForm.Item
                    validateStatus={errors.re_email ? 'error' : ''}
                    help={errors.re_email?.message}
                  >
                    <Controller
                      name="re_email"
                      control={control}
                      render={({ field }) => (
                        <AppTextField
                          {...field}
                          id="contact-re-email"
                          type="email"
                          className="w-full"
                          onBlur={e => {
                            field.onChange(normalizeEmailInput(e.target.value));
                            field.onBlur();
                          }}
                        />
                      )}
                    />
                  </AppForm.Item>
                </AppCol>
              </AppRow>
            </AppCol>
          </AppRow>
        </form>
      </AppCard>

      {/* Submit: fixed bottom action bar */}
      <BottomBar
        variant="action"
        right={
          <AppButton
            id="contact-submit"
            type="primary"
            htmlType="submit"
            className="rounded"
            form="contact-edit-form"
            loading={isPending}
            disabled={isPending}
            style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
          >
            <i className="mr-2 fa fa-paper-plane" aria-hidden="true" /> 送信
          </AppButton>
        }
      />
    </div>
  );
};

export default ContactEditPage;
